import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'motion/react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Users, Target, Award, ExternalLink } from 'lucide-react';
import Layout from '../components/layout/Layout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Text Reveal Animation
const TextReveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

// Case Study Card
const CaseStudyCard = ({ study, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-30px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="group"
    >
      <Link to={`/case-studies/${study.id}`} data-testid={`case-study-card-${study.id}`}>
        <motion.div 
          className="card-fresh overflow-hidden h-full cursor-pointer"
          whileHover={{ y: -8 }}
        >
          {/* Image */}
          <div className="relative h-56 -mx-8 -mt-8 mb-6 overflow-hidden">
            <img
              src={study.image}
              alt={study.client}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
            
            {/* Category Badge */}
            <span className="absolute top-4 left-4 px-3 py-1 bg-gradient-to-r from-[#FF6B35] to-[#FFB627] text-white text-xs font-semibold rounded-full">
              {study.category}
            </span>
            
            {/* Date Badge */}
            <span className="absolute top-4 right-4 px-3 py-1 bg-white/90 text-[#5A5A5A] text-xs font-medium rounded-full">
              {study.date}
            </span>
          </div>

          {/* Content */}
          <div className="relative z-10">
            <p className="text-[#FF6B35] text-sm font-medium mb-2">{study.client}</p>
            <h3 className="font-display text-xl font-semibold text-[#2D2D2D] mb-3 group-hover:text-[#FF6B35] transition-colors">
              {study.title}
            </h3>
            <p className="text-[#5A5A5A] text-sm leading-relaxed mb-4 line-clamp-2">
              {study.description}
            </p>

            {/* Results */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              {study.results.map((result, i) => (
                <div key={i} className="text-center p-2 bg-[#FFFBF5] rounded-lg">
                  <p className="font-display text-lg font-bold text-[#FF6B35]">{result.value}</p>
                  <p className="text-[#8A8A8A] text-xs">{result.label}</p>
                </div>
              ))}
            </div>

            {/* View Details Link */}
            <div className="flex items-center justify-between pt-4 border-t border-[#FF6B35]/10">
              <div className="flex flex-wrap gap-1">
                {study.services.slice(0, 2).map((service, i) => (
                  <span key={i} className="px-2 py-1 bg-white text-[#5A5A5A] text-xs rounded-full border border-[#FF6B35]/10">
                    {service}
                  </span>
                ))}
                {study.services.length > 2 && (
                  <span className="px-2 py-1 text-[#8A8A8A] text-xs">
                    +{study.services.length - 2} more
                  </span>
                )}
              </div>
              <span className="flex items-center gap-1 text-[#FF6B35] text-sm font-medium group-hover:gap-2 transition-all">
                View Details
                <ExternalLink size={14} />
              </span>
            </div>
          </div>
        </motion.div>
      </Link>
    </motion.div>
  );
};

const CaseStudies = () => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCaseStudies();
  }, []);

  const fetchCaseStudies = async () => {
    try {
      const response = await fetch(`${API_URL}/api/case-studies`);
      const data = await response.json();
      setCaseStudies(data);
    } catch (error) {
      console.error('Error fetching case studies:', error);
    } finally {
      setLoading(false);
    }
  };

  // Stats
  const stats = [
    { icon: Award, value: "50+", label: "Projects Completed" },
    { icon: TrendingUp, value: "285%", label: "Avg. Growth" },
    { icon: Users, value: "40+", label: "Happy Clients" },
    { icon: Target, value: "98%", label: "Success Rate" }
  ];

  return (
    <Layout>
      {/* HERO SECTION */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-sunrise-hero relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <motion.div 
            className="absolute top-20 right-[15%] w-[500px] h-[500px] sun-orb sun-orb-primary"
            animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 8, repeat: Infinity }}
          />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-4xl">
            <TextReveal>
              <span className="inline-block px-4 py-1.5 bg-[#FF6B35]/10 text-[#FF6B35] text-sm font-medium rounded-full mb-6">Case Studies</span>
            </TextReveal>
            
            <TextReveal delay={0.1}>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#2D2D2D] leading-[1.05] mb-4">
                Real results for
              </h1>
            </TextReveal>
            <TextReveal delay={0.15}>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-8">
                <span className="text-gradient-sunrise">real businesses.</span>
              </h1>
            </TextReveal>
            
            <TextReveal delay={0.2}>
              <p className="text-lg md:text-xl text-[#5A5A5A] max-w-2xl leading-relaxed">
                Explore how we've helped businesses across industries achieve remarkable growth 
                through our integrated approach to marketing, design, and technology.
              </p>
            </TextReveal>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-12 bg-white border-y border-[#FF6B35]/10">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <TextReveal key={stat.label} delay={index * 0.1}>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF6B35]/10 to-[#FFB627]/10 flex items-center justify-center mx-auto mb-3">
                    <stat.icon className="text-[#FF6B35]" size={24} />
                  </div>
                  <p className="font-display text-3xl font-bold text-[#FF6B35]">{stat.value}</p>
                  <p className="text-[#8A8A8A] text-sm">{stat.label}</p>
                </div>
              </TextReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CASE STUDIES GRID */}
      <section className="section-padding bg-[#FFFBF5]">
        <div className="container mx-auto px-6 md:px-12">
          <TextReveal>
            <div className="flex items-center justify-between mb-12">
              <div>
                <h2 className="font-display text-3xl md:text-4xl text-[#2D2D2D]">
                  Latest <span className="text-gradient-sunrise">Success Stories</span>
                </h2>
                <p className="text-[#5A5A5A] mt-2">From the last 3 months</p>
              </div>
            </div>
          </TextReveal>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="card-fresh animate-pulse">
                  <div className="h-56 -mx-8 -mt-8 mb-6 bg-[#FF6B35]/5 rounded-t-2xl" />
                  <div className="h-4 bg-[#FF6B35]/5 rounded w-1/3 mb-2" />
                  <div className="h-6 bg-[#FF6B35]/5 rounded mb-3" />
                  <div className="h-4 bg-[#FF6B35]/5 rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {caseStudies.map((study, index) => (
                <CaseStudyCard key={study.id || study.client} study={study} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="section-padding bg-gradient-to-br from-[#FF6B35] to-[#FFB627] relative overflow-hidden">
        <div className="absolute inset-0">
          <motion.div 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)' }}
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 8, repeat: Infinity }}
          />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <TextReveal>
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="font-display text-4xl md:text-5xl text-white mb-4">
                Want results like these?
              </h2>
              <h2 className="font-display text-4xl md:text-5xl text-white/90 mb-8">
                Let's make it happen.
              </h2>
              <p className="text-lg text-white/80 max-w-xl mx-auto mb-10">
                Every success story started with a conversation. 
                Tell us about your goals and let's create your success story together.
              </p>
              <Link to="/contact">
                <motion.button
                  className="px-10 py-5 bg-white text-[#FF6B35] rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-shadow inline-flex items-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Start Your Success Story
                  <ArrowRight size={20} />
                </motion.button>
              </Link>
            </div>
          </TextReveal>
        </div>
      </section>
    </Layout>
  );
};

export default CaseStudies;
